import type { ComponentPropsWithoutRef } from "react";
import { useCallback, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import {
  CANVAS_SIZE_ID,
  DESKTOP_WIDTH,
  MOBILE_WIDTH,
  TABLET_WIDTH,
} from "./constants";

export interface CanvasSizeProps extends ComponentPropsWithoutRef<"div"> {
  desktopWidth?: number;
  mobileWidth?: number;
  tabletWidth?: number;
}

export const CanvasZoomableSize = ({
  children,
  className,
  mobileWidth = MOBILE_WIDTH,
  tabletWidth = TABLET_WIDTH,
  desktopWidth = DESKTOP_WIDTH,
  ...rest
}: CanvasSizeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { canvasSize, isFullscreenPreview, level, updateLevel } =
    useBuilderStore(state => ({
      canvasSize: state.canvasSize,
      isFullscreenPreview: state.isFullscreenPreview,
      level: state.zoom.level,
      updateLevel: state.zoom.updateLevel,
    }));

  let width = desktopWidth;
  if (canvasSize === "mobile") width = mobileWidth;
  if (canvasSize === "tablet") width = tabletWidth;
  const isPreviewAndDesktopSize =
    canvasSize === "desktop" && isFullscreenPreview;

  const updateZoomLevel = useCallback(() => {
    if (!containerRef.current) {
      return;
    }
    const containerWidth = containerRef.current.clientWidth;

    const zoomLevel = containerWidth / width;
    const clampedZoomLevel = Math.max(0, Math.min(1, zoomLevel));
    const roundedZoomLevel = Math.round(clampedZoomLevel * 100) / 100;

    updateLevel(roundedZoomLevel);
  }, [containerRef, width, updateLevel]);

  useEffect(() => {
    const debouncedUpdateZoomLevel = debounce(() => {
      updateZoomLevel();
    }, 200);

    const onResize = () => {
      debouncedUpdateZoomLevel();
    };

    window.addEventListener("resize", onResize);
    updateZoomLevel();

    return () => {
      window.removeEventListener("resize", onResize);
      debouncedUpdateZoomLevel.cancel();
    };
  }, [updateZoomLevel, isFullscreenPreview]);

  return (
    <div
      id={CANVAS_SIZE_ID}
      ref={containerRef}
      className="relative flex size-full justify-center"
      data-testid="canvas-zoomable-size"
    >
      <div
        className={classNames(
          "absolute size-full origin-top transition-all",
          className,
        )}
        style={{
          width: isPreviewAndDesktopSize ? "100%" : `${width}px`,
          transform: `scale(${level})`,
        }}
        data-testid="canvas-zoomable-size-inner"
        {...rest}
      >
        {children}
      </div>
    </div>
  );
};
